<template>
  <div
    v-if="isArrayNotEmpty(locales) && locales.length > 1"
    class="block-navigation-language-dropdown"
    :class="{ 'is-open': isOpen }"
  >
    <div 
      class="block-navigation-language-dropdown__current"
    >
      {{ locales.find((lang) => lang.code === locale)?.name }}
    </div>

    <button 
      type="button"
      class="block-navigation-trigger" 
      @click="toggleOpen(false)"
      @keypress="toggleOpen(false)"
    />

    <ul
      ref="optionsRef"
      class="block-navigation-language-dropdown__list"
    >
      <li
        v-for="lang in inactiveLocales"
        :key="lang.code"
        class="block-navigation-language-dropdown__list-item"
        :class="{ 'is-active': locale === lang.code }"
      >
        <NuxtLink
          v-if="!isStorybook"
          class="block-navigation-language__list-item-link"
          :to="processLocalePath(lang.code)"
        >
          {{ lang.name }}
        </NuxtLink>

        <div
          v-else
          class="block-navigation-language__list-item-link"
          @click="changeLocale(lang.code)"
          @keypress="changeLocale(lang.code)"
        >
          {{ lang.name }}
        </div>
      </li>
    </ul>

    <IonIcon
      icon-name="chevron-down"
      class="block-navigation-language-dropdown__icon"
      :icon-color="textColor"
      icon-size="10px"
    />
  </div>
</template>

<script setup>
const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'var(--c-primary-200)',
    },

    textColor: {
        type: String,
        default: 'var(--c-primary-500)',
    },

    borderRadius: {
        type: String,
        default: 'var(--b-radius--full)',
    },
});

/*
    Styles
*/
const {
    backgroundColor,
    textColor,
} = toRefs(props);

/*
    Internationalization
*/
const switchLocalePath = useSwitchLocalePath();
const { locale, locales } = useI18n();
const currentLocale = ref(locale.value);

const inactiveLocales = computed(
    () => locales.value?.filter((lang) => lang.code !== currentLocale.value) || [],
);

const runtimeConfig = useRuntimeConfig();
const isStorybook = runtimeConfig.public.IS_STORYBOOK;

/*
  Handle home link
*/
const processLocalePath = (langCode) => {
    let path = switchLocalePath(langCode);

    // Remove query params
    path = path.replace(/\?.*$/, '');

    if (path.endsWith('/home')) {
        path = path.replace(/home$/, '');
    }

    return path;
};

watch(() => locale.value, (newValue) => {
    window.setTimeout(() => {
        currentLocale.value = newValue;
    }, 0);
});

/* Storybook Only */
const changeLocale = (newLocale) => {
    locale.value = newLocale;
};

/*
    Dropdown
*/
const closedHeight = '0px';
const optionsHeight = ref(closedHeight);
const optionsRef = ref(null);

const isOpen = ref(false);
const toggleOpen = (forceClose) => {
    isOpen.value = forceClose ? false : !isOpen.value;
    optionsHeight.value = isOpen.value ? `${optionsRef.value.scrollHeight}px` : closedHeight;
};

/* Watch window width to set height of box accordingly on resize */
const { width } = useWindowSize();
watch(() => width.value, () => {
    if (!props.isFullScreen) {
        optionsHeight.value = isOpen.value ? `${optionsRef.value.scrollHeight}px` : closedHeight;
    }
});

watch(() => locale.value, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    toggleOpen(true);
  }
})
</script>

<style lang="scss" scoped>

.block-navigation-language-dropdown {
    @include fluid('--padding-top', 3px, 3px, 3px);
    // @include fluid('--font-size', 13px, 12px, 15px);

    --font-size: var(--f-size--link);

    position: relative;
    display: inline-flex;
    flex-direction: column;
    padding: 5px 18px;
    padding-right: 30px;
    border-radius: 18px;
    background-color: v-bind(backgroundColor);
    color: v-bind(textColor);
    cursor: pointer;
    font-family: var(--f-family--primary);
    font-size: var(--f-size--button);
    font-weight: var(--f-weight--500);
    line-height: var(--l-height--button);
    text-transform: uppercase;

}

.block-navigation-language__list-item-link {
    display: flex;
    width: 100%;
    height: 100%;
}

.block-navigation-trigger {
    position: absolute;
    z-index: 4;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 100px;
}

.block-navigation-language-dropdown__current {
    z-index: 2;
    min-width: 24px;
}

.block-navigation-language-dropdown__list {

    position: absolute;
    z-index: 1;
    top: 18px;
    left: 0;
    display: flex;
    overflow: hidden;
    width: 100%;
    max-height: v-bind(optionsHeight);
    flex-direction: column;
    padding-top: 10px;
    padding-right: 43px;
    padding-left: 18px;
    border-radius: 18px;
    background: v-bind(backgroundColor);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    // row-gap: 10px;
    transition: max-height 0.3s var(--ease-in-out--quart) 0.15s, opacity 0.3s var(--ease-in-out--sine);

    .is-open & {
        opacity: 1;
        transition: max-height 0.3s var(--ease-in-out--quart), opacity 0.3s var(--ease-in-out--sine) 0.15s;
    }
}

.block-navigation-language-dropdown__list-item {
    width: 35px;
    opacity: 0;
    transition: opacity 0.3s var(--ease-in-out--sine);
    @include fluid('padding-top', 4px, 4px, 4px);
    @include fluid('padding-bottom', 4px, 4px, 4px);

    .is-open & {
        opacity: 1;
        transition: opacity 0.3s var(--ease-in-out--sine) 0.15s;
    }

    &:last-child {
        @include fluid('margin-bottom', 6px, 6px, 6px);
    }

    &:first-child {
        @include fluid('margin-top', 6px, 6px, 6px);
    }
}

.block-navigation-language-dropdown__icon {
    position: absolute;
    z-index: 2;
    top: 16px;
    right: 15px;
    pointer-events: none;
    transition: transform 0.3s var(--ease-in-out--quart);

    .is-open & {
        transform: rotate(180deg) translateY(1px);
    }

    ::v-deep(.ion-icon__svg) {
        @include fluid('height', 8px, 8px, 8px);
    }
}
</style>
